<template>
  <Page v-if="ready" title="Memberships">
    <TabSwitcher id="memberships" :tabs="tabs" />
  </Page>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useFeatureFlag, useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import Page from "@/components/Page.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import MembershipHealthDashboard from "@/MembershipHealthDashboard/MembershipHealthDashboard.vue";
import MembershipsSold from "@/Reports/MembershipsSold.vue";
import PaymentActivity from "@/Reports/PaymentActivity.vue";
import ServiceUtilization from "@/Reports/ServiceUtilization.vue";

// membership_tabs_enabled gates the Payment Activity tab.
// membership_health_dashboard_enabled gates the Dashboard tab and makes
// it the default landing tab. Each rollout can ship independently.
const { enabled: paymentActivityEnabled, resolved: tenantResolved } =
  useResolvedFeatureFlag("membership_tabs_enabled");
const dashboardEnabled = useFeatureFlag("membership_health_dashboard_enabled");

const initialData = ref<{
  filters: [];
  source: string;
} | null>(null);

const failedPaymentsData = ref<{
  filters: [];
  source: string;
  failed_payments_count?: number;
} | null>(null);

// Settles on failure too, so a failed request cannot hold the page back.
const failedPaymentsSettled = ref(false);

// A table mounted with no source throws on its first draw.
const pageReady = computed(
  () =>
    Boolean(initialData.value) &&
    tenantResolved.value &&
    (!paymentActivityEnabled.value || failedPaymentsSettled.value)
);

// A later tenant refresh must not tear down a page that is in use.
const ready = ref(false);
watch(
  pageReady,
  (isReady) => {
    if (isReady) ready.value = true;
  },
  { immediate: true }
);

const tabs = computed(() => [
  {
    name: "Dashboard",
    component: MembershipHealthDashboard,
    visible: Boolean(dashboardEnabled.value)
  },
  {
    name: "Membership To-Do",
    component: MembershipsSold,
    props: initialData.value
  },
  {
    name: "Payment Activity",
    component: PaymentActivity,
    props: {
      source: failedPaymentsData.value?.source || "",
      filters: failedPaymentsData.value?.filters || []
    },
    badge: failedPaymentsData.value?.failed_payments_count ?? null,
    visible: Boolean(paymentActivityEnabled.value && failedPaymentsData.value)
  },
  {
    name: "Membership Service Utilization",
    component: ServiceUtilization
  }
]);

const baseUrl = `${window.location.origin}${window.location.pathname}`;

onMounted(async () => {
  const res = await fetch(`${baseUrl}/initial_page_data`, { method: "GET" });
  initialData.value = await res.json();
});

// This endpoint 404s unless membership_tabs_enabled is on, and on a cold load
// the flag is unknown until the tenant arrives, after mount.
let failedPaymentsRequested = false;
watch(
  [tenantResolved, paymentActivityEnabled],
  async ([resolved, enabled]) => {
    if (!resolved || !enabled || failedPaymentsRequested) return;
    failedPaymentsRequested = true;
    try {
      const res = await fetch(`${baseUrl}/failed_payments_initial_page_data`, {
        method: "GET"
      });
      if (res.ok) failedPaymentsData.value = await res.json();
    } catch {
      // The tab stays hidden; the rest of the page still renders.
    } finally {
      failedPaymentsSettled.value = true;
    }
  },
  { immediate: true }
);
</script>
